import React, { useState, ReactNode, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { styled } from 'styled-components';
import classNames from 'classnames';
import Icon, { IconProps } from '../../../icon';
import { Flex } from '../../../box';
import Button from '../../../button';
import { H6 } from '../../../title';
import Collapse from '../../../collapse';
import { colors, darken, spacing } from '../../../../style';
import { actionLight } from '../../../../style/theme-helpers';
import Text from '../../../text';
import { useSidebarContext } from '../sidebar-context';

type SidebarItemsGroupProps = {
  title: string;
  icon: IconProps['name'];
  children: ReactNode;
  'data-testid'?: string;
};

type GroupContainerProps = {
  children: ReactNode;
};

type SubItemProps = {
  title: string;
  path?: string;
  className?: string;
  'data-testid'?: string;
};

function SidebarItemsGroup({ title, icon, children, 'data-testid': dataTestId }: SidebarItemsGroupProps) {
  const [isOpen, setIsOpen] = useState(true);

  const { mode, onItemFocus, activePath } = useSidebarContext();
  const isGroupActive = useMemo(() => {
    // eslint-disable-next-line @eslint-react/no-children-to-array
    return React.Children.toArray(children).some((child: ReactNode) => {
      if (React.isValidElement(child) && 'props' in child) {
        const childProps = child.props as SubItemProps;
        return childProps.path && activePath && activePath?.includes(childProps.path);
      }
      return false;
    });
  }, [children, activePath]);

  return (
    <Flex padding={mode === 'collapsed' ? 'xs' : 'none'} flexDirection={'column'}>
      <Button
        style={{
          borderRadius: mode === 'collapsed' ? 8 : undefined,
        }}
        block
        active={isGroupActive && mode === 'collapsed'}
        onFocus={onItemFocus}
        size={'auto'}
        variant={'secondary-clean'}
        onClick={() => setIsOpen(!isOpen)}
        data-testid={dataTestId}
      >
        <Flex
          paddingRight={'xs'}
          paddingLeft={mode === 'collapsed' ? 'xs' : 'sm'}
          justifyContent={mode === 'collapsed' ? 'center' : 'space-between'}
          alignItems={'center'}
          paddingVertical={'xs'}
          flexGrow={1}
        >
          <Flex alignItems={'center'}>
            <Icon name={icon} size={'xxl'} outlined={!isGroupActive} />

            <div hidden={mode === 'collapsed'}>
              <H6 marginVertical={'none'} marginLeft={'sm'}>
                {title}
              </H6>
            </div>
          </Flex>

          {mode === 'expanded' && (
            <Icon
              size={'md'}
              name={'CHEVRON_DOWN'}
              style={{
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'all 0.2s ease-out allow-discrete',
              }}
            />
          )}
        </Flex>
      </Button>

      <Collapse isOpen={isOpen}>
        <div hidden={mode === 'collapsed'}>
          <Flex
            flexDirection={'column'}
            gap={'xxs'}
            paddingVertical={'xxs'}
            paddingRight={'xs'}
            marginLeft={'sm'}
            paddingLeft={'lg'}
          >
            {children}
          </Flex>
        </div>
      </Collapse>
    </Flex>
  );
}

function GroupContainer({ children }: GroupContainerProps) {
  return children;
}

const StyledSubItem = styled(Flex)`
  flex-grow: 1;
  padding: ${spacing.xs};
  color: ${colors.black};
  transition: background 0.2s ease-out;
  background: transparent;

  &:hover {
    background: ${colors.gray100};
  }

  &.active {
    background: ${actionLight};

    &:hover {
      background: ${props => darken(0.05, actionLight(props))};
    }
  }
`;
const SubItem = ({ title, path, className, 'data-testid': dataTestId }: SubItemProps) => {
  const { onItemFocus, setActivePath } = useSidebarContext();
  return (
    <StyledSubItem
      paddingVertical={'xs'}
      paddingLeft={'xs'}
      alignItems={'center'}
      borderRadiusRight={'lg'}
      className={classNames('nav-group-item', className)}
      componentClass={NavLink}
      onFocus={onItemFocus}
      onClick={() => {
        setActivePath(path || '');
      }}
      to={path || ''}
      data-testid={dataTestId}
    >
      <Text variant={'inherit'} size={'md'}>
        {title}
      </Text>
    </StyledSubItem>
  );
};

SidebarItemsGroup.GroupContainer = GroupContainer;
SidebarItemsGroup.SubItem = SubItem;

export { SidebarItemsGroup };
